// @ts-nocheck
import React from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router';
import Data, { propTypes as DataPropTypes } from 'components/grid/data';
import List, { ListComponents } from '../list';
import { propTypes as ListPropTypes } from 'components/grid/list/components/list';
import { isDefined } from 'utils';
import { getPropsFromTypes, watchForEffectInputs } from 'utils/react';
import { useListContext } from 'components/grid/api-list/hooks/list-context';
import { assign, isPlainObject, isArray, noop, pick } from 'lodash';
import { set, isFunction } from 'lodash/fp';
import { withForwardRef } from 'hoc';

const watchInputs = [
  'sort',
  'filter',
  'perPage',
  'page',
  'search',
  'variables',
];

interface FetchInput {
  filter?: any;
  page?: number;
  perPage?: number;
  search: any;
  variables: any;
  sort: any;
}

function ApiList({ forwardedRef, ...props }) {
  const [state, setState] = useListContext(props);

  const dataElement = React.useRef<any>();

  React.useEffect(() => {
    if (dataElement.current) {
      dataElement.current.fetch(getVariables());
    }
  }, watchForEffectInputs(state, watchInputs));

  React.useImperativeHandle(forwardedRef, () => ({
    fetch(input: FetchInput) {
      const parsedInput = pick(input, watchInputs);
      setState((previousState) => ({ ...previousState, ...parsedInput }));
    },
    reFetch() {
      dataElement.current.reFetch();
    },
  }));

  function serializeFilters(filters) {
    return Object.entries(filters).reduce((sanitized, [name, value]) => {
      if (isPlainObject(value)) {
        value = serializeFilters(value);
      }
      if (isArray(value)) {
        value = value.join(',');
      }
      return { ...sanitized, [name]: value };
    }, {});
  }

  function getVariables() {
    const {
      filter,
      sort,
      perPage,
      page,
      search = {},
      variables,
      prepareVariables,
    } = state;

    const input = {
      filter: serializeFilters(filter),
      sort,
      offset: { page, perPage },
    };

    if (isDefined(search.fields) && isDefined(search.query)) {
      input.filter = set('search', search, input.filter);
    }
    // This is helper prop in case we need some manipulation on variables object...example: src/routes/content/offers/index.js:20
    if (isFunction(prepareVariables)) {
      return prepareVariables(assign(variables, { input }));
    }

    return assign(variables, { input });
  }

  const { children } = props;

  const dataProps = getPropsFromTypes(state, DataPropTypes);
  const listProps = getPropsFromTypes(state, ListPropTypes);

  return (
    <Data
      ref={dataElement}
      {...dataProps}
      waitForData={false}
      variables={getVariables()}>
      {({ data, loading }) => {
        const { data: items, meta, error } = data || {};

        return (
          <List
            {...listProps}
            loading={loading}
            items={items}
            meta={meta}
            error={error}>
            <List.Loader />
            {children}
          </List>
        );
      }}
    </Data>
  );
}

ApiList.propTypes = {
  children: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
  defaultData: PropTypes.any,
  withRouter: PropTypes.bool,
  sortToString: PropTypes.func,
  sort: PropTypes.string,
  filter: PropTypes.object,
  location: PropTypes.object,
  page: PropTypes.number,
  perPage: PropTypes.number,
  search: PropTypes.object,
  variables: PropTypes.object,
  prepareVariables: PropTypes.func,
  ref: PropTypes.func,
  poolInterval: PropTypes.number,
};

ApiList.defaultProps = {
  children: null,
  defaultData: [],
  withRouter: false,
  sortToString: noop,
  sort: '',
  filter: {},
  location: {},
  page: 1,
  perPage: 10,
  search: undefined,
  variables: {},
  prepareVariables: undefined,
  ref: undefined,
  poolInterval: undefined,
};

const ApiListComponent = Object.assign(
  withForwardRef(withRouter(withForwardRef(ApiList))),
  ListComponents,
);

ApiListComponent.propTypes = {
  ...ListPropTypes,
  ...DataPropTypes,
};

export default ApiListComponent;
